import { SegmentedFilter } from '@easydoctor/ui';
import {
  TICKET_LIST_SCOPE_VIEWS,
  isScopeView,
  ticketListViewDescription,
  ticketListViewLabel,
  type TicketListScope,
  type TicketListView,
} from '@easyhelp/shared';
import Box from '@mui/material/Box';
import type { MouseEvent } from 'react';

interface QueueViewsProps {
  /** Coda o archivio (#123): ciascuno ha le sue viste. */
  scope: TicketListScope;
  active: TicketListView;
  /** Contatori coerenti con filtri e ricerca attivi; assenti al primo caricamento. */
  counts: Partial<Record<TicketListView, number>> | undefined;
  onSelect: (view: TicketListView) => void;
}

/**
 * Viste rapide con contatore (PRD-004 US-1): `SegmentedFilter` di `@easydoctor/ui`, con «Etichetta (N)»
 * e la descrizione di ciascuna vista nel tooltip (al passaggio del mouse e al fuoco da tastiera), che
 * è anche la descrizione accessibile del pulsante. La coda ha Tutti · Senza priorità · Operatori ·
 * Pre-accesso · Allo sviluppo, l'archivio Tutti · Risolti · Chiusi · Scartati (#123). Nessuna vista
 * «Pazienti» (DEC-005) né per prodotto (DEC-003). I contatori contano i ticket di ciascuna vista con i
 * filtri attivi.
 */
export function QueueViews({ scope, active, counts, onSelect }: QueueViewsProps) {
  const options = TICKET_LIST_SCOPE_VIEWS[scope].map((view) => {
    const label = ticketListViewLabel(scope, view);
    const count = counts?.[view];
    return {
      value: view,
      label,
      count,
      // Il nome accessibile è il testo del pulsante, contatore compreso: chi usa un lettore di schermo
      // sente quanti ticket ha la vista, come chi la vede.
      ariaLabel: count === undefined ? label : `${label} (${count})`,
      description: ticketListViewDescription(scope, view),
    };
  });

  // Ripremere la vista già attiva la riapplica e torna alla pagina 1, come faceva il Chip. `ToggleButtonGroup`
  // in quel caso passa `null` e `SegmentedFilter` lo scarta senza chiamare `onChange`: il clic si legge qui,
  // dal pulsante ancora premuto (per le altre viste `aria-pressed` cambia solo al render successivo).
  const reselect = (event: MouseEvent<HTMLElement>) => {
    const button = (event.target as HTMLElement).closest('button');
    if (button?.getAttribute('aria-pressed') === 'true') onSelect(active);
  };

  return (
    // Su un telefono, o in una card stretta, le viste non stanno in una riga: `SegmentedFilter`
    // le manda a capo da sé (dalla 0.2.1 di `@easydoctor/ui`) invece di allargare la pagina.
    <Box onClick={reselect}>
      <SegmentedFilter
        aria-label="Viste rapide"
        activeValue={active}
        options={options}
        onChange={(value) => {
          if (isScopeView(scope, value)) onSelect(value);
        }}
      />
    </Box>
  );
}
